import React from "react";
import {
  PageList,
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../../../components/pageList";
import { useCellRender } from "../../../hooks/useCellRender";
import ActionCellRender from "../../../components/actionCellRender";
import {
  IconPencil,
  IconSettings,
} from "@tabler/icons";
import { Textarea } from "@mantine/core";
import ActivityLog from "../../../components/activityLog.js";
import AutoCodeInput from "../../../components/autoCodeInput";
import FormConfirmation from "../../../components/formConfirmation";
import FileList from "../../../components/fileList";
import Payroll2Stat from "./Payroll2Stat";
import PayrollSearchTabs from "./PayrollSearchTabs";
import AccountingSourceTab from "../../../components/accountingSourceTab";

const apiEntity = "payroll";
const initialValues = {};

const addFormSchema = {
  title: "New Payroll",
  display: {
    mode: ENUM_FORM_DISPLAY.MODAL,
    size: "xl",
  },
  showSaveBar: false,
  apiEntity,
  initialValues,
  layout: {
    containers: [
      {
        key: "t1",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.WIZARD,
        props: {
          variant: "default",
          defaultValue: "Basic Info",
          orientation: "vertical",
          style: { minHeight: "400px" },
        },
        steps: [
          {
            key: "t1-1",
            label: "Basic Info",
            icon: IconSettings,
          },
          { key: "t1-3", label: "Confirm", icon: IconSettings },
        ],
      },
      {
        key: "section-info",
        parent: "t1-1",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Payroll Info",
          description: "Please fill in the basic information of the payroll",
          withBorder: false,
          ml: 20,
          mt: 0,
        },
      },
      {
        key: "section-confirm",
        parent: "t1-3",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Confirm",
          description: "Please confirm the statement details",
          withBorder: false,
          ml: 20,
          mt: 0,
        },
      },
    ],
    fields: [
      {
        name: "code",
        component: AutoCodeInput,
        parent: "section-info",
        props: {
          required: true,
          placeholder: "PR-202212",
          label: "Patch ID",
        },
      },
      {
        name: "importRemark",
        component: Textarea,
        parent: "section-info",
        props: {
          placeholder: "",
          label: "Remark",
        },
      },
      {
        name: "confirm",
        component: FormConfirmation,
        parent: "section-confirm",
        props: {
          successText: ["Please confirm if to create the new payroll."],
        },
      },
    ],
  },
};

const editFormSchema = {
  titleField: "code",
  display: {
    mode: ENUM_FORM_DISPLAY.LINK,
    size: "xl",
  },
  apiEntity,
  initialValues,
  layout: {
    containers: [
      {
        key: "main-tab",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
        props: {
          variant: "default",
          defaultValue: "Payroll",
        },
        tab: [
          {
            key: "tab1",
            label: "Payroll",
            icon: IconSettings,
          },
          {
            key: "tab2",
            label: "Files",
            icon: IconSettings,
          },
          {
            key: "tab3",
            label: "Logs",
            icon: IconSettings,
          },
          {
            key: "tab4",
            label: "Accounting",
            icon: IconSettings,
          },
        ],
      },
      {
        key: "section-files",
        parent: "tab2",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Files",
          titleOrder: 5,
          withBorder: false,
          minHeight: "0px",
          mt: 20,
          mb: 20,
        },
      },
      {
        key: "section-payroll",
        parent: "tab1",
        type: ENUM_FORM_LAYOUT_CONTAINER.BOX,
        props: {
          mt: 20,
        },
      },
      {
        key: "section-log",
        parent: "tab3",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Activity Log",
          titleOrder: 5,
          withBorder: false,
          minHeight: 0,
          mt: 20,
          mb: 20,
        },
      },
      {
        key: "section-accounting",
        parent: "tab4",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Accounting",
          withBorder: false,
          minHeight: 0,
          mt: 20,
          mb: 20,
        },
      },
    ],
    fields: [
      {
        name: "payrollStat",
        parent: "section-payroll",
        component: Payroll2Stat,
        props: {},
      },
      {
        name: "searchTabs",
        parent: "section-payroll",
        component: PayrollSearchTabs,
        props: {},
      },
      {
        name: "files",
        component: FileList,
        parent: "section-files",
        props: {},
      },
      {
        name: "logs",
        component: ActivityLog,
        parent: "section-log",
        props: {},
      },
      {
        name: "accounting",
        component: AccountingSourceTab,
        parent: "section-accounting",
        props: {
          sourceType: "payroll",
        },
      },
    ],
  },
};

const Payroll2 = () => {
  const [cellRender] = useCellRender();

  const tableSchema = {
    showActionColumn: true,
    select: [
      "code totalBids totalAmount remark paid totalSettledAmount status",
    ],
    columns: [
      { field: "code", headerName: "Code", sortable: true },
      {
        field: "totalAmount",
        headerName: "Total Payments",
        cellRender: (col, data) => cellRender.Currency(data.totalAmount),
        sortable: true,
      },
      {
        field: "paid",
        headerName: "Paid Amount",
        sortable: true,
        cellRender: (col, data) =>
          cellRender.Currency(data.totalSettledAmount ?? data.paid),
      },
      { field: "totalBids", headerName: "Bids", sortable: true },
      {
        field: "status",
        cellRender: (col, data) => cellRender.Status(data.status),
      },
      {
        field: "action",
        cellRender: ActionCellRender,
        isAction: true,
        actions: [
          {
            name: "edit",
            icon: IconPencil,
            props: {},
          },
        ],
      },
    ],
  };

  const formSchema = {
    add: addFormSchema,
    edit: editFormSchema,
  };

  return (
    <PageList
      title={"Payroll List"}
      initSort={{ by: "createdAt", order: "desc" }}
      apiEntity={apiEntity}
      tableSchema={tableSchema}
      formSchema={formSchema}
    />
  );
};

export default Payroll2;
